import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES } from "@/data/content";

// Numbered services with hover image reveal (light theme)
export default function Services() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="services"
      data-testid="services"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="mb-16 md:mb-20 max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
            <span className="w-8 h-px bg-brand-gold" /> Nos services
          </span>
          <h2 className="mt-5 font-display font-semibold text-4xl md:text-6xl tracking-tight text-brand-navy leading-[1.05]">
            Un serrurier pour chaque situation
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Dépannage, sécurisation, installation : nos artisans interviennent
            rapidement et proprement, avec un devis clair avant toute action.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_440px] gap-12">
          <div className="grid sm:grid-cols-2 gap-4">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 2) * 0.06}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-${s.slug}`}
                    onMouseEnter={() => setActive(i)}
                    className="group block h-full rounded-2xl border border-brand-line bg-white p-7 hover:border-brand-navy/20 hover:shadow-xl hover:shadow-brand-navy/5 transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-start justify-between">
                      <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-navy/5 text-brand-navy group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors duration-300">
                        <Icon className="w-6 h-6" />
                      </span>
                      <span className="font-display text-sm text-slate-300 group-hover:text-brand-gold transition-colors duration-300">
                        {s.num}
                      </span>
                    </div>
                    <h3 className="mt-5 font-display font-medium text-xl text-brand-navy">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {s.desc}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-brand-golddark">
                      {s.tagline}{" "}
                      <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          {/* Sticky image */}
          <div className="hidden lg:block">
            <div className="sticky top-28 h-[540px] rounded-2xl overflow-hidden border border-brand-line shadow-xl shadow-brand-navy/5">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={SERVICES[active].image}
                  alt={SERVICES[active].title}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/20 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-8">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-gold">
                  {SERVICES[active].tagline}
                </span>
                <div className="mt-1 font-display font-semibold text-3xl tracking-tight text-white">
                  {SERVICES[active].title}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
